import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    newUrl:      { type: String, default: "" },
    slotHeight:  { type: Number, default: 48 },
    slotMinutes: { type: Number, default: 30 },
    dayStart:    { type: Number, default: 7 },
    saveError:   { type: String, default: "The reservation could not be moved." }
  }

  static targets = ["feedback"]

  connect() {
    this.startCurrentTimeTicker()
  }

  disconnect() {
    clearInterval(this._ticker)
    this.removeInteractionListeners()
  }

  slotClick(e) {
    if (this._suppressNextClick) {
      e.preventDefault()
      e.stopImmediatePropagation()
      this._suppressNextClick = false
      return
    }

    const col = e.target.closest("[data-bay-id]")
    if (!col || col.dataset.bayAvailable === "false" || e.target.closest(".bay-reservation")) return

    const rect = col.getBoundingClientRect()
    const slotIndex = Math.floor((e.clientY - rect.top) / this.slotHeightValue)
    const startMin = this.dayStartValue * 60 + slotIndex * this.slotMinutesValue
    const endMin = startMin + this.slotMinutesValue * 2
    const params = new URLSearchParams(window.location.search)
    const date = col.dataset.date || params.get("date") || this.localDate(new Date())
    const view = params.get("view") || "day"

    const url = new URL(this.newUrlValue, window.location.origin)
    url.searchParams.set("bay_id", col.dataset.bayId)
    url.searchParams.set("date", date)
    url.searchParams.set("view", view)
    url.searchParams.set("starts_at", this.toDateTime(date, startMin))
    url.searchParams.set("ends_at", this.toDateTime(date, endMin))

    const btn = Object.assign(document.createElement("button"), {
      type: "button", hidden: true
    })
    btn.dataset.bsToggle = "modal"
    btn.dataset.bsTarget = "#calendarModal"
    btn.dataset.url = url.toString()
    document.body.appendChild(btn)
    btn.click()
    btn.remove()
  }

  // ── Drag and resize ──────────────────────────────────────────────────────

  guardClick(event) {
    if (!this._suppressNextClick) return

    event.preventDefault()
    event.stopImmediatePropagation()
    this._suppressNextClick = false
  }

  interactionStart(event) {
    if (event.button !== 0 || this._saving) return

    this._suppressNextClick = false
    const reservation = event.currentTarget
    const grid = reservation.closest("[data-bay-id]")
    if (!grid || grid.dataset.bayAvailable === "false") return

    const reservationRect = reservation.getBoundingClientRect()
    const weekEvent = reservation.classList.contains("bay-calendar-week__event")
    this._interaction = {
      reservation,
      mode: weekEvent ? "weekMove" : (event.target.closest("[data-resize-handle]") ? "resize" : "move"),
      startX: event.clientX,
      startY: event.clientY,
      pointerOffsetY: event.clientY - reservationRect.top,
      originalGrid: grid,
      targetGrid: grid,
      originalDate: reservation.dataset.date,
      originalTop: reservation.style.top,
      originalHeight: reservation.style.height,
      started: false
    }

    this._interactionMove = this.interactionMove.bind(this)
    this._interactionEnd = this.interactionEnd.bind(this)
    window.addEventListener("pointermove", this._interactionMove, { passive: false })
    window.addEventListener("pointerup", this._interactionEnd)
    window.addEventListener("pointercancel", this._interactionEnd)
  }

  interactionMove(event) {
    const state = this._interaction
    if (!state) return

    if (!state.started) {
      if (Math.hypot(event.clientX - state.startX, event.clientY - state.startY) < 4) return

      state.started = true
      state.reservation.classList.add(
        state.mode === "resize" ? "bay-reservation--resizing" : "bay-reservation--dragging"
      )
      this.hideFeedback()
    }

    event.preventDefault()

    if (state.mode === "resize") {
      this.previewResize(state, event.clientY)
    } else if (state.mode === "weekMove") {
      this.previewWeekMove(state, event.clientX, event.clientY)
    } else {
      this.previewMove(state, event.clientX, event.clientY)
    }
  }

  interactionEnd() {
    const state = this._interaction
    this.removeInteractionListeners()
    if (!state) return

    state.reservation.classList.remove("bay-reservation--dragging", "bay-reservation--resizing")
    state.targetGrid.classList.remove("bay-calendar-week__cell--drop-target")
    if (!state.started) return

    this._suppressNextClick = true
    this.persistInteraction(state)
  }

  eventClick(event) {
    event.preventDefault()
    event.stopImmediatePropagation()

    if (event.target.closest("[data-resize-handle]")) return
    if (this._suppressNextClick) {
      this._suppressNextClick = false
      return
    }

    const modalElement = document.querySelector("#calendarModal")
    if (modalElement && window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(modalElement).show(event.currentTarget)
    } else {
      window.location.assign(event.currentTarget.href)
    }
  }

  previewMove(state, clientX, clientY) {
    const targetGrid = this.availableGridAt(clientX)
    if (!targetGrid) return

    const gridRect = targetGrid.getBoundingClientRect()
    const height = state.reservation.getBoundingClientRect().height
    const maxTop = Math.max(0, gridRect.height - height)
    const top = this.snap(Math.min(Math.max(clientY - gridRect.top - state.pointerOffsetY, 0), maxTop))

    state.targetGrid = targetGrid
    state.reservation.style.top = `${top}px`
  }

  previewResize(state, clientY) {
    const reservationRect = state.reservation.getBoundingClientRect()
    const gridRect = state.originalGrid.getBoundingClientRect()
    const maxHeight = gridRect.bottom - reservationRect.top
    const heightWithGap = this.snap(Math.min(Math.max(clientY - reservationRect.top, this.slotHeightValue), maxHeight))

    state.reservation.style.height = `${Math.max(heightWithGap - 4, 0)}px`
  }

  previewWeekMove(state, clientX, clientY) {
    const targetGrid = this.availableWeekCellAt(clientX, clientY)
    if (!targetGrid || targetGrid === state.targetGrid) return

    state.targetGrid.classList.remove("bay-calendar-week__cell--drop-target")
    state.targetGrid = targetGrid
    state.targetGrid.classList.add("bay-calendar-week__cell--drop-target")
  }

  async persistInteraction(state) {
    const reservation = state.reservation
    const moving = state.mode === "move" || state.mode === "weekMove"
    const targetGrid = moving ? state.targetGrid : state.originalGrid
    const weekMove = state.mode === "weekMove"
    const top = Number.parseFloat(reservation.style.top) || 0
    const height = Number.parseFloat(reservation.style.height) || 0
    const startMinutes = weekMove ? Number(reservation.dataset.startMinutes) :
      this.dayStartValue * 60 + Math.round(top / this.slotHeightValue) * this.slotMinutesValue
    const durationMinutes = weekMove ? Number(reservation.dataset.durationMinutes) :
      Math.max(1, Math.round((height + 4) / this.slotHeightValue)) * this.slotMinutesValue
    const endMinutes = startMinutes + durationMinutes
    const date = targetGrid.dataset.date

    if (moving && reservation.parentElement !== targetGrid) targetGrid.appendChild(reservation)
    reservation.dataset.date = date
    this._saving = true

    try {
      const response = await fetch(reservation.dataset.interactionUrl, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
        },
        body: JSON.stringify({
          calendar_reservation: {
            bay_id: targetGrid.dataset.bayId,
            starts_at: this.toDateTime(date, startMinutes),
            ends_at: this.toDateTime(date, endMinutes),
            lock_version: reservation.dataset.lockVersion
          }
        })
      })
      const payload = await response.json().catch(() => ({}))

      if (!response.ok) throw new Error(payload.error || this.saveErrorValue)

      reservation.dataset.lockVersion = payload.lock_version
      reservation.dataset.date = payload.starts_at.slice(0, 10)
      if (weekMove) {
        this.element.querySelectorAll(`[data-reservation-id="${payload.id}"]`).forEach((card) => {
          if (card !== reservation) card.remove()
        })
      }
    } catch (error) {
      this.restoreInteraction(state)
      this.showFeedback(error.message || this.saveErrorValue)
    } finally {
      this._saving = false
    }
  }

  restoreInteraction(state) {
    state.originalGrid.appendChild(state.reservation)
    state.reservation.style.top = state.originalTop
    state.reservation.style.height = state.originalHeight
    state.reservation.dataset.date = state.originalDate
  }

  availableGridAt(clientX) {
    return Array.from(this.element.querySelectorAll("[data-bay-id][data-bay-available='true']")).find((grid) => {
      const rect = grid.getBoundingClientRect()
      return clientX >= rect.left && clientX <= rect.right
    })
  }

  availableWeekCellAt(clientX, clientY) {
    return Array.from(this.element.querySelectorAll(".bay-calendar-week__cell[data-bay-available='true']")).find((cell) => {
      const rect = cell.getBoundingClientRect()
      return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
    })
  }

  removeInteractionListeners() {
    if (this._interactionMove) window.removeEventListener("pointermove", this._interactionMove)
    if (this._interactionEnd) {
      window.removeEventListener("pointerup", this._interactionEnd)
      window.removeEventListener("pointercancel", this._interactionEnd)
    }
    this._interaction = null
    this._interactionMove = null
    this._interactionEnd = null
  }

  snap(pixels) {
    return Math.round(pixels / this.slotHeightValue) * this.slotHeightValue
  }

  showFeedback(message) {
    if (!this.hasFeedbackTarget) return

    this.feedbackTarget.textContent = message
    this.feedbackTarget.hidden = false
  }

  hideFeedback() {
    if (this.hasFeedbackTarget) this.feedbackTarget.hidden = true
  }

  // ── Current time indicator ────────────────────────────────────────────────

  startCurrentTimeTicker() {
    this.updateCurrentTimeLine()
    this._ticker = setInterval(() => this.updateCurrentTimeLine(), 60_000)
  }

  updateCurrentTimeLine() {
    const line = this.element.querySelector(".bay-calendar__now-line")
    if (!line) return

    const now    = new Date()
    const grid   = line.closest("[data-date]")
    const date   = grid?.dataset.date || new URLSearchParams(window.location.search).get("date") || new Date().toISOString().split("T")[0]
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`
    const isToday  = date === todayStr

    if (!isToday) { line.style.display = "none"; return }

    const top = (now.getHours() * 60 + now.getMinutes() - this.dayStartValue * 60)
        / this.slotMinutesValue * this.slotHeightValue

    line.style.display = top >= 0 ? "block" : "none"
    line.style.top     = `${top}px`
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  toTime(minutes) {
    const minutesInDay = minutes % (24 * 60)
    return `${Math.floor(minutesInDay/60).toString().padStart(2,"0")}:${(minutesInDay%60).toString().padStart(2,"0")}`
  }

  toDateTime(date, minutes) {
    const [year, month, day] = date.split("-").map(Number)
    const value = new Date(Date.UTC(year, month - 1, day, 0, minutes))
    return `${value.toISOString().slice(0, 10)}T${this.toTime(minutes)}:00`
  }

  localDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }
}
